// ============================================================================
// VYVA Onboarding Portal - Header Component
// ============================================================================

import { Bell, Settings, LogOut } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import vyvaLogo from '@/assets/logo-purple.png';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { useAuthStore, useTenantStore, useNotificationsStore } from '@/store';
import { Badge } from './ui/badge';
import { BACKEND_URL } from '@/constants/public';
import type { Tenant } from '@/types';
import { useBulkOnboardingStore } from '@/store/useBulkOnboardingStore';


export function Header() {
  const navigate = useNavigate();
  const { user, logout } = useAuthStore();
  const { currentTenant, setCurrentTenant } = useTenantStore();
  const { unreadCount } = useNotificationsStore();

  const defaultTenants: Tenant[] = useMemo(
    () => [
      { id: 'zamora', name: 'Zamoras', slug: 'zamora', isActive: true },
      { id: 'cocoon', name: 'COCOON', slug: 'cocoon', isActive: true },
      { id: 'red-cross', name: 'Red Cross', slug: 'red-cross', isActive: true },
    ],
    []
  );

  const [tenants, setTenants] = useState<Tenant[]>(defaultTenants);
  const [isLoadingTenants, setIsLoadingTenants] = useState(false);
  const [seeded, setSeeded] = useState(false);

  useEffect(() => {
    const fetchOrganizations = async () => {
      setIsLoadingTenants(true);
      try {
        const res = await fetch(`${BACKEND_URL}/api/v1/organizations/`);
        const json = await res.json();

        if (json?.success && Array.isArray(json.data) && json.data.length > 0) {
          const mapped: Tenant[] = json.data.map((org: { id: string | number; name: string }) => ({
            id: String(org.id),
            name: org.name,
            slug: org.name?.toLowerCase().replace(/\s+/g, '-') || String(org.id),
            isActive: true,
          }));
          setTenants(mapped);
          if (!currentTenant && mapped[0]) {
            setCurrentTenant(mapped[0]);
            setSelectedOrganization(mapped[0].name);
            setSeeded(true);
          }
        } else {
          setTenants(defaultTenants);
          if (!currentTenant && defaultTenants[0]) {
            setCurrentTenant(defaultTenants[0]);
            setSelectedOrganization(defaultTenants[0].name);
            setSeeded(true);
          }
        }
      } catch (error) {
        console.error('Failed to fetch organizations:', error);
        setTenants(defaultTenants);
        if (!currentTenant && defaultTenants[0]) {
          setCurrentTenant(defaultTenants[0]);
          setSeeded(true);
        }
      }
      setIsLoadingTenants(false);
    };

    if (!seeded) {
      fetchOrganizations();
    }
  }, [currentTenant, defaultTenants, seeded, setCurrentTenant]);

const setSelectedOrganization = useBulkOnboardingStore(
  (state) => state.setSelectedOrganization
);

const handleTenantChange = (tenantId: string) => {
  const tenant = tenants.find(t => t.id === tenantId);
  if (tenant) {
    setCurrentTenant(tenant);
    setSelectedOrganization(tenant.name); // ✅ sync Zustand
  }
};

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const userInitials = user 
    ? `${user.firstName[0]}${user.lastName[0]}`.toUpperCase()
    : 'U';

  return (
    <header className="h-16 border-b border-border bg-card flex items-center justify-between px-6">
      {/* Left Section */}
      <div className="flex items-center gap-8">
        <img 
          src={vyvaLogo} 
          alt="VYVA" 
          className="h-8 cursor-pointer" 
          onClick={() => navigate('/dashboard')}
        />
        
        <Select 
          value={currentTenant?.id || tenants[0]?.id}
          onValueChange={handleTenantChange}
          disabled={isLoadingTenants || tenants.length === 0}
        >
          <SelectTrigger className="w-[200px]">
            <SelectValue placeholder={isLoadingTenants ? 'Loading...' : 'Select tenant'} />
          </SelectTrigger>
          <SelectContent>
            {tenants?.map((tenant) => (
              <SelectItem key={tenant.id} value={tenant.id}>
                {tenant.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Right Section */}
      <div className="flex items-center gap-4">
        {/* Notifications */}
        <Button variant="ghost" size="icon" className="relative">
          <Bell className="h-5 w-5 text-muted-foreground" />
          {unreadCount > 0 && (
            <Badge 
              className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 bg-red-500 text-[10px]"
            >
              {unreadCount > 9 ? '9+' : unreadCount}
            </Badge>
          )}
        </Button>

        {/* Settings Quick Access */}
        <Button 
          variant="ghost" 
          size="icon"
          onClick={() => navigate('/settings')}
        >
          <Settings className="h-5 w-5 text-muted-foreground" />
        </Button>

        {/* Logout Button */}
        <Button 
          variant="ghost" 
          size="icon"
          onClick={handleLogout}
          className="text-muted-foreground hover:text-red-600 hover:bg-red-50"
        >
          <LogOut className="h-5 w-5" />
        </Button>

        {/* User Menu */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="relative h-9 w-9 rounded-full">
              <Avatar className="h-9 w-9">
                <AvatarImage src={user?.avatar} alt={user?.firstName} />
                <AvatarFallback className="bg-primary text-primary-foreground">
                  {userInitials}
                </AvatarFallback>
              </Avatar>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuLabel className="font-normal">
              <div className="flex flex-col space-y-1">
                <p className="text-sm font-medium leading-none">
                  {user?.firstName} {user?.lastName}
                </p>
                <p className="text-xs leading-none text-muted-foreground">
                  {user?.email}
                </p>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => navigate('/settings')}>
              Settings
            </DropdownMenuItem>
            <DropdownMenuItem>
              Help & Support
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem 
              className="text-red-600 focus:text-red-600"
              onClick={handleLogout}
            >
              Sign out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}

export default Header;
